import { webOrigin } from './env';
import { compareVersions } from './version';

/** O que o site publica (`/downloads/version.json`, gerado junto com o APK): a versão do APK e como conferi-lo. */
export interface LatestApp {
  version: string;
  /** Endereço completo do APK (sempre na mesma origem do site). */
  apkUrl: string;
  sha256: string;
  size: number;
  /** "O que há de novo" desta versão (opcional; vem do `-Notes` do script de build). */
  notes: string;
}

/** Tamanho máximo do texto de novidades mostrado no aviso. */
const NOTES_MAX = 500;

/** Valida o JSON publicado; qualquer coisa fora do formato é ignorada (melhor não avisar do que avisar errado). */
export function parseLatest(json: unknown, origin: string = webOrigin()): LatestApp | null {
  if (typeof json !== 'object' || json === null) {
    return null;
  }

  const data = json as Record<string, unknown>;
  const { version, apk, sha256, size } = data;
  if (
    typeof version !== 'string' ||
    !/^\d+\.\d+\.\d+$/.test(version) ||
    typeof apk !== 'string' ||
    typeof sha256 !== 'string' ||
    !/^[0-9a-f]{64}$/i.test(sha256)
  ) {
    return null;
  }

  let apkUrl: URL;
  try {
    apkUrl = new URL(apk, `${origin}/`);
  } catch {
    return null;
  }

  // O APK só pode vir do próprio site: um version.json adulterado não pode apontar para outro lugar.
  if (apkUrl.origin !== new URL(origin).origin) {
    return null;
  }

  return {
    version,
    apkUrl: apkUrl.toString(),
    sha256: sha256.toLowerCase(),
    size: typeof size === 'number' ? size : 0,
    notes: typeof data.notes === 'string' ? data.notes.trim().slice(0, NOTES_MAX) : '',
  };
}

export type FetchText = (url: string) => Promise<Response>;

/** Pergunta ao site qual é a versão mais nova. Devolve `null` se não deu para saber (sem rede, arquivo ausente ou fora do formato). */
export async function fetchLatest(
  fetcher: FetchText = (url) => fetch(url, { cache: 'no-store' }),
  origin: string = webOrigin(),
): Promise<LatestApp | null> {
  try {
    const response = await fetcher(`${origin}/downloads/version.json`);
    if (!response.ok) {
      return null;
    }

    return parseLatest(await response.json(), origin);
  } catch {
    return null;
  }
}

/** Há uma versão mais nova do que a que está rodando? */
export function isNewer(latest: LatestApp, current: string): boolean {
  return compareVersions(latest.version, current) > 0;
}
